import React, { useCallback, useEffect, useState } from 'react';
import {
  passkeysAvailable,
  enrolPasskey,
  listPasskeys,
  removePasskey,
  wasCancelled,
  deviceLabel,
  EnrolledPasskey,
} from '../utils/passkeys';
import { formatDateDdMmmYy } from '../utils/format';

/**
 * Manage Face ID / fingerprint sign-in for the signed-in user.
 *
 * The list is the point of this panel as much as the button is: someone who
 * loses a phone needs to see it named here and be able to revoke it without
 * asking an administrator.
 */
const PasskeySettings: React.FC = () => {
  const [supported, setSupported] = useState<boolean | null>(null);
  const [passkeys, setPasskeys] = useState<EnrolledPasskey[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    listPasskeys()
      .then((r) => setPasskeys(r.data))
      .catch(() => setPasskeys([]));
  }, []);

  useEffect(() => {
    passkeysAvailable().then(setSupported);
    load();
  }, [load]);

  const handleEnrol = async () => {
    setMessage('');
    setError('');
    setBusy(true);
    try {
      await enrolPasskey();
      setMessage(`${deviceLabel()} can now sign you in.`);
      load();
    } catch (err: any) {
      if (!wasCancelled(err)) {
        setError(err.response?.data?.message || 'Could not set up this device. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (p: EnrolledPasskey) => {
    const name = p.label || 'this device';
    if (!window.confirm(`Remove ${name}? It will no longer be able to sign in with a fingerprint or face.`)) return;
    setMessage('');
    setError('');
    try {
      await removePasskey(p.id);
      setMessage(`${name} removed.`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not remove that device');
    }
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-900">Face ID & fingerprint</h2>
      <p className="text-sm text-gray-500 mt-1 leading-relaxed">
        Sign in with the fingerprint reader or face unlock your device already uses,
        instead of typing a password. Your fingerprint never leaves your device — it
        stays on the phone and only unlocks a key stored there.
      </p>

      {supported === false && (
        <div className="mt-4 bg-gray-50 border border-gray-200 text-gray-600 text-sm px-4 py-3 rounded-lg">
          This device doesn't offer a built-in fingerprint or face unlock, so there's
          nothing to set up here. You can still set it up on your phone.
        </div>
      )}

      {message && (
        <div className="mt-4 bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3 rounded-lg">
          {message}
        </div>
      )}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {passkeys.length > 0 && (
        <ul className="mt-4 divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {passkeys.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{p.label || 'Unnamed device'}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Added {formatDateDdMmmYy(p.createdAt)}
                  {p.lastUsedAt ? ` · last used ${formatDateDdMmmYy(p.lastUsedAt)}` : ' · not used yet'}
                </p>
              </div>
              <button
                onClick={() => handleRemove(p)}
                className="text-sm font-medium text-red-600 hover:text-red-700 shrink-0"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {supported && (
        <button onClick={handleEnrol} disabled={busy} className="btn-primary mt-4">
          {busy
            ? 'Waiting for your device…'
            : passkeys.length > 0
              ? 'Set up another device'
              : `Set up on ${deviceLabel()}`}
        </button>
      )}

      {passkeys.length > 0 && (
        <p className="text-xs text-gray-400 mt-3 leading-relaxed">
          Lost a device? Remove it here and it can no longer sign in. Your password and
          emailed sign-in codes keep working everywhere.
        </p>
      )}
    </div>
  );
};

export default PasskeySettings;
